import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Image, Skeleton, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { Favorite, FavoriteBorder, IosShare, ShoppingBagOutlined, WhatsApp } from '@mui/icons-material';
import StarRatings from 'react-star-ratings';

import Breadcrumb from '../components/layout/Breadcrumb';
import Comment from '../components/Comment';
import ReviewModal from '../components/ReviewModal';
import SizeSelector from '../components/product/SizeSelector';
import AddedLabel from '../components/product/AddedLabel';
import ProductGrid from '../components/product/ProductGrid';
import { ProductGallery, ZoomModal, QuantityStepper, PincodeCheck, SizeChartModal, PdpAccordions } from '../components/product/PdpParts';
import { formatPrice } from '../components/product/productUtils';
import { useCartContext } from '../contexts/CartContext';
import { useUserContext } from '../contexts/UserContext';
import { useShopUI } from '../contexts/ShopUIContext';
import useCatalog from '../hooks/useCatalog';
import useGetUserHaveThis from '../hooks/useGetUserHaveThis';
import { getProductById } from '../services/ProductServices';
import { getCommentByProductId } from '../services/CommentServices';
import { getRatingByProductId } from '../services/RatingServices';
import { STORE, whatsappLink } from '../config/store';

const condensedHeading = { fontWeight: 700, textTransform: 'uppercase', sx: { fontStretch: '75%' } };

// Product page (design-system.md §5.4): gallery left, sticky details right on desktop; stacked on phones
const Product = () => {

  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  // Shared links (/product/:id) arrive without router state
  const productId = params.id || location.state?.productId;

  const { addItem } = useCartContext();
  const { currentUser } = useUserContext();
  const { isFavorite, toggleFavorite } = useShopUI();
  const { categories, products } = useCatalog();
  const review = useDisclosure();
  const sizeChart = useDisclosure();
  const [have] = useGetUserHaveThis(currentUser, productId);

  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [rating, setRating] = useState(0);
  const [ratingCount, setRatingCount] = useState(0);
  const [comments, setComments] = useState([]);
  const [size, setSize] = useState('');
  const [sizeError, setSizeError] = useState(false);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    if (!productId) { setNotFound(true); return; }
    setProduct(null); setNotFound(false); setSize(''); setQty(1); setAdded(false);
    window.scrollTo(0, 0);
    getProductById(productId)
      .then((result) => result.product ? setProduct(result.product) : setNotFound(true))
      .catch(() => setNotFound(true));
    getRatingByProductId(productId)
      .then((result) => {
        const total = result.ratings.reduce((sum, r) => sum + r.rating, 0);
        setRating(total / result.ratings.length || 0);
        setRatingCount(result.ratings.length);
      })
      .catch(() => { });
    getCommentByProductId(productId)
      .then((result) => setComments(result.comment || []))
      .catch(() => { });
  }, [productId]);

  useEffect(() => { if (product?.sizes?.length === 1) setSize(product.sizes[0]); }, [product]);

  const category = categories.find(c => c._id === product?.category);
  const images = product ? (product.images?.length ? product.images : [product.imageUrl]) : [];
  const related = useMemo(() => {
    if (!product) return [];
    const same = products.filter(p => p._id !== product._id && p.category === product.category);
    const others = products.filter(p => p._id !== product._id && p.category !== product.category);
    return [...same, ...others].slice(0, 4);
  }, [products, product]);

  const needSize = () => {
    setSizeError(true);
    document.getElementById('size-picker')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const addToCart = () => {
    if (!size) return needSize();
    addItem(product, size, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const buyNow = () => {
    if (!size) return needSize();
    addItem(product, size, qty);
    navigate('/cart');
  };

  const shareUrl = `${window.location.origin}/product/${productId}`;
  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title: product.name, text: `${product.name} at Raano`, url: shareUrl });
      else { await navigator.clipboard.writeText(shareUrl); toast({ title: 'Link copied', status: 'success', duration: 1500, position: 'top' }); }
    } catch (e) { }
  };

  const writeReview = () => {
    if (have) review.onOpen();
    else toast({ title: 'Only buyers can review', description: 'You can review this product after your order is delivered.', status: 'info', duration: 2500 });
  };

  if (notFound) {
    return (
      <Box textAlign='center' py='80px' px='16px' >
        <Text fontSize='28px' {...condensedHeading} >Product not found</Text>
        <Text color='raano.muted' mt='8px' >It may have been removed.</Text>
        <Button mt='20px' variant='ink' height='44px' px='28px' onClick={() => navigate('/search', { state: {} })} >Shop all</Button>
      </Box>
    );
  }

  const iconBtn = { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };

  return (
    <Box>
      <Box display={{ base: 'none', md: 'block' }} >
        <Breadcrumb items={[
          { label: 'Home', to: '/' },
          { label: 'Shop All', to: '/search', state: {} },
          ...(category ? [{ label: category.name, to: '/search', state: { categoryId: category._id } }] : []),
          { label: product?.name || '…' },
        ]} />
      </Box>

      <Box display='flex' flexDirection={{ base: 'column', md: 'row' }} gap={{ base: 0, md: '48px' }} px={{ base: 0, md: '24px', lg: '40px' }} pt={{ base: 0, md: '0' }} alignItems='flex-start' >
        {/* Gallery */}
        <Box flex={1} minWidth={0} width='100%' >
          {product ? <ProductGallery images={images} name={product.name} onZoom={setZoom} /> : <Skeleton sx={{ aspectRatio: '2 / 3' }} maxWidth={{ md: '560px' }} />}
        </Box>

        {/* Details */}
        <Box width={{ base: '100%', md: '420px', lg: '600px' }} flexShrink={0} position={{ md: 'sticky' }} top={{ md: '138px' }} px={{ base: '12px', md: 0 }} pt={{ base: '16px', md: '16px' }} pb={{ base: '8px', md: '40px' }} >
          {product ? <>
            <Box display='flex' alignItems='flex-start' gap='8px' >
              <Text as='h1' flex={1} textStyle='pdpTitle' >{product.name}</Text>
              <Box as='button' {...iconBtn} aria-label={isFavorite(product._id) ? 'Remove from wishlist' : 'Add to wishlist'} aria-pressed={isFavorite(product._id)} onClick={() => toggleFavorite(product._id)} >
                {isFavorite(product._id) ? <Favorite /> : <FavoriteBorder />}
              </Box>
              <Box as='button' {...iconBtn} aria-label='Share this page' onClick={share} ><IosShare sx={{ fontSize: 22 }} /></Box>
            </Box>

            {ratingCount > 0 &&
              <Box as='a' href='#reviews' display='inline-flex' alignItems='center' gap='8px' mt='8px' >
                <StarRatings starDimension='14px' starSpacing='1px' rating={rating} starRatedColor='#000000' starEmptyColor='#D9D9D9' numberOfStars={5} name='rating' />
                <Text fontSize='13px' color='raano.muted' >{rating.toFixed(1)} · {ratingCount} {ratingCount === 1 ? 'review' : 'reviews'}</Text>
              </Box>}

            <Box display='flex' alignItems='baseline' gap='8px' mt='14px' pb={{ base: '16px', md: '22px' }} borderBottom='1px solid' borderColor='raano.border' >
              <Text fontSize='16px' fontWeight={500} >{formatPrice(product.price, { space: false })}</Text>
              <Text fontSize='12px' color='raano.muted' >Inclusive of all taxes</Text>
            </Box>

            {/* Colour */}
            <Box mt={{ base: '18px', md: '26px' }} >
              <Text fontSize={{ base: '14px', md: '16px' }} color='blackAlpha.600' textTransform='capitalize' >Color: <Box as='span' color='raano.black' >{product.color}</Box></Text>
              <Box mt='10px' width='68px' >
                <Box border='1px solid' borderColor='raano.black' p='2px' >
                  <Image src={product.imageUrl} alt={product.color} width='100%' sx={{ aspectRatio: '2 / 3' }} objectFit='cover' />
                </Box>
                <Text fontSize='12px' textAlign='center' mt='4px' textTransform='capitalize' noOfLines={1} >{product.color}</Text>
              </Box>
            </Box>

            {/* Size */}
            <Box id='size-picker' mt={{ base: '20px', md: '28px' }} >
              <SizeSelector sizes={product.sizes} value={size} onChange={(s) => { setSize(s); setSizeError(false); }} onSizeChart={sizeChart.onOpen} />
              {sizeError && <Text mt='8px' fontSize='13px' color='raano.error' >Please select a size.</Text>}
            </Box>

            {/* Quantity + buy buttons */}
            <Box display='flex' flexWrap={{ base: 'wrap', md: 'nowrap' }} gap={{ base: '10px', md: '16px' }} mt={{ base: '20px', md: '24px' }} >
              <QuantityStepper value={qty} onChange={setQty} />
              <Button variant='wipe' flex={1} height={{ base: '36px', md: '43px' }} fontSize={{ base: '16px', md: '18px' }} onClick={addToCart} >
                <AddedLabel added={added} ><ShoppingBagOutlined sx={{ fontSize: 20 }} />Add to cart</AddedLabel>
              </Button>
              <Button variant='ink' flex={{ base: '1 0 100%', md: 1 }} height={{ base: '36px', md: '43px' }} fontSize={{ base: '16px', md: '18px' }} isDisabled={!size} onClick={buyNow} >Buy it now</Button>
            </Box>

            <Box as='a' href={whatsappLink(`Hi Raano, I have a question about ${product.name} (₹${product.price}): ${shareUrl}`)} target='_blank' rel='noreferrer' display='inline-flex' alignItems='center' gap='6px' mt='14px' fontSize='14px' textDecoration='underline' >
              <WhatsApp sx={{ fontSize: 18 }} /> Ask about this on WhatsApp
            </Box>

            <PincodeCheck />

            <PdpAccordions sections={[
              { title: 'Product Details', body: <><Text>{product.description}</Text><Text mt='8px' textTransform='capitalize' ><b>Colour:</b> {product.color}</Text></> },
              { title: 'Delivery & Returns', body: <Text>Free delivery on every order, usually in 3–5 days. Returns and exchanges within 30 days of delivery for unworn items with tags. Payments are secure: UPI, cards and netbanking.</Text> },
              {
                title: 'More Information', body: <>
                  <Text><b>Sizes:</b> {(product.sizes || []).join(', ')}</Text>
                  {category && <Text><b>Category:</b> {category.name}</Text>}
                  <Text><b>Sold by:</b> {STORE.name}</Text>
                  <Text><b>Country of origin:</b> India</Text>
                </>
              },
            ]} />

            {/* Reviews */}
            <Box id='reviews' mt={{ base: '28px', md: '36px' }} >
              <Box display='flex' alignItems='center' justifyContent='space-between' >
                <Text as='h2' fontSize='18px' {...condensedHeading} >Customer Reviews</Text>
                <Text as='button' fontSize='14px' fontWeight={500} textDecoration='underline' onClick={writeReview} >Write a review</Text>
              </Box>
              <Box display='flex' alignItems='center' gap='10px' mt='10px' >
                <StarRatings starDimension='16px' starSpacing='1px' rating={rating} starRatedColor='#000000' starEmptyColor='#D9D9D9' numberOfStars={5} name='avg' />
                <Text fontSize='14px' color='raano.muted' >{ratingCount ? `${rating.toFixed(1)} out of 5 · ${ratingCount} ${ratingCount === 1 ? 'rating' : 'ratings'}` : 'Be the first to write a review'}</Text>
              </Box>
              <Box mt='8px' >
                {comments.map(c => <Comment key={c._id} authorId={c.author} commentText={c.comment} createdAt={c.createdAt} />)}
              </Box>
            </Box>
          </> : <>
            <Skeleton height='28px' width='80%' />
            <Skeleton height='20px' width='20%' mt='16px' />
            <Skeleton height='40px' mt='32px' />
            <Skeleton height='44px' mt='24px' />
          </>}
        </Box>
      </Box>

      {/* You may also like */}
      {related.length > 0 &&
        <Box px={{ base: '12px', md: '40px' }} pt={{ base: '32px', md: '56px' }} pb={{ base: '24px', md: '48px' }} >
          <Text as='h2' fontSize={{ base: '22px', md: '30px' }} {...condensedHeading} mb={{ base: '14px', md: '22px' }} >You may also like</Text>
          <ProductGrid products={related} />
        </Box>}

      <SizeChartModal isOpen={sizeChart.isOpen} onClose={sizeChart.onClose} sizes={product?.sizes} />
      <ZoomModal images={images} start={zoom} onClose={() => setZoom(null)} name={product?.name} />
      <ReviewModal isOpen={review.isOpen} onClose={review.onClose} productId={productId} />
    </Box>
  );
};

export default Product;
